import React, { useEffect, useState } from "react";

import { Check, Edit2, Info, Plus, ShoppingBag, Trash2, X } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Skeleton } from "~/components/ui/skeleton";
import { toast } from "~/hooks/use-toast";
import { useProducts } from "~/hooks/useProducts";
import { supabase } from "~/integrations/supabase/client";
import { useProfileStore } from "~/store/profileStore";
import { SalesRequestItem } from "~/types/client";
import { PriceRange } from "~/types/product";
import { formatCurrency, formatUnitPrice } from "~/utils/helpers";
import { calcLineTotal } from "~/utils/lineItemCalculations";

interface OrderItemsTableProps {
  items: SalesRequestItem[];
  editable?: boolean;
  salesRequestId?: string;
  orderStatus?: string;
  onItemsChange?: (
    items: SalesRequestItem[],
    newTotalAmount?: number,
    newPriceWithIva?: number
  ) => void;
}

// Interface for product details needed in the table
interface ProductDetails {
  id: string;
  name: string;
  image?: string;
  details?: string;
  unit?: string;
}

// Interface for available products
interface AvailableProduct {
  id: string;
  short_name: string;
  long_name: string;
  product_image_url?: string;
  unit_code?: string;
  base_measurement_unit?: string;
  discount_percent?: number;
  visible_to_sellers?: boolean | null;
}

export const OrderItemsTable: React.FC<OrderItemsTableProps> = ({
  items,
  editable = false,
  salesRequestId,
  orderStatus,
  onItemsChange,
}) => {
  const [productDetails, setProductDetails] = useState<
    Record<string, ProductDetails>
  >({});
  // Per-line name captured at factura emission (sales_request_items.product_name_snapshot).
  // Keyed by item id (not product id) so each line keeps the name it was invoiced with,
  // even when the same catalog product was renamed between facturas. Falls back to the
  // live product name when absent (drafts / lines not yet invoiced).
  const [nameSnapshots, setNameSnapshots] = useState<Record<string, string>>(
    {}
  );
  const [availableProducts, setAvailableProducts] = useState<
    AvailableProduct[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedQuantity, setEditedQuantity] = useState<number>(0);
  const [editedUnitPrice, setEditedUnitPrice] = useState<number>(0);
  const [editedProductId, setEditedProductId] = useState<string>("");
  const [editedDiscountPercent, setEditedDiscountPercent] = useState<number>(0);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);

  // State for adding new products
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newProductId, setNewProductId] = useState<string>("");
  const [newQuantity, setNewQuantity] = useState<number>(1);
  const [newUnitPrice, setNewUnitPrice] = useState<number>(0);
  const [newDiscountPercent, setNewDiscountPercent] = useState<number>(0);
  const [addingSaving, setAddingSaving] = useState(false);

  // Use the products hook to get price ranges
  const { priceRanges } = useProducts();
  const { role } = useProfileStore();

  // Sellers must not be able to attach products hidden from them
  // (visible_to_sellers === false). This mirrors the create-flow guard in
  // ProductSelector, which only covers the creation surface — the order-detail
  // edit table was an unguarded second product-selection point. The line's
  // current product is always kept so the edit <select> doesn't blank out when
  // an existing line already holds a hidden product.
  const getSelectableProducts = (currentProductId?: string) =>
    availableProducts.filter(
      (product) =>
        role !== "seller" ||
        product.visible_to_sellers !== false ||
        product.id === currentProductId
    );
  const [currentProductPriceRanges, setCurrentProductPriceRanges] = useState<
    PriceRange[]
  >([]);
  const [newProductPriceRanges, setNewProductPriceRanges] = useState<
    PriceRange[]
  >([]);

  // Fetch product details for all products and available products for editing
  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        setLoading(true);

        // Fetch all available products for product selection
        if (editable) {
          const { data: allProducts, error: allProductsError } = await supabase
            .from("products")
            .select(
              "id, short_name, long_name, product_image_url, unit_code, base_measurement_unit, discount_percent, visible_to_sellers"
            )
            .order("short_name");

          if (allProductsError) throw allProductsError;
          setAvailableProducts(allProducts || []);
        }

        if (!items || items.length === 0) {
          setLoading(false);
          return;
        }

        // Fetch the emission-time name snapshot per line (keyed by item id). Reports must
        // show what was invoiced, not the product's current catalog name.
        const itemIds = items.map((item) => item.id).filter(Boolean);
        if (itemIds.length > 0) {
          const { data: snapshotRows } = await supabase
            .from("sales_request_items")
            .select("id, product_name_snapshot")
            .in("id", itemIds);

          const snapshots: Record<string, string> = {};
          snapshotRows?.forEach((row) => {
            if (row.product_name_snapshot) {
              snapshots[row.id] = row.product_name_snapshot;
            }
          });
          setNameSnapshots(snapshots);
        }

        // Get unique product IDs
        const productIds = [...new Set(items.map((item) => item.productId))];

        // Fetch product details for current items
        const { data, error } = await supabase
          .from("products")
          .select(
            "id, short_name, long_name, product_image_url, unit_code, base_measurement_unit"
          )
          .in("id", productIds);

        if (error) throw error;

        // Create a map of product details
        const details: Record<string, ProductDetails> = {};
        data?.forEach((product) => {
          details[product.id] = {
            id: product.id,
            name: product.long_name || product.short_name,
            image: product.product_image_url,
            details:
              product.short_name !== product.long_name
                ? product.short_name
                : undefined,
            unit: product.unit_code || product.base_measurement_unit,
          };
        });

        // Add fallback details for products not found in the database
        items.forEach((item, index) => {
          if (!details[item.productId]) {
            details[item.productId] = {
              id: item.productId,
              name: `Producto ${index + 1}`,
              image: undefined,
              details: `ID: ${item.productId}`,
              unit: item.unit,
            };
          }
        });

        setProductDetails(details);
      } catch (error) {
        console.error("Error fetching product details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [items, editable]);

  const handleEdit = (index: number, item: SalesRequestItem) => {
    // Cancel any new product addition
    handleCancelNew();

    setEditingIndex(index);
    setEditedQuantity(item.quantity);
    setEditedUnitPrice(item.unitPrice);
    setEditedProductId(item.productId);
    setEditedDiscountPercent(item.discountPercent ?? 0);

    // Fetch price ranges for this product
    setCurrentProductPriceRanges(
      priceRanges.filter((range) => range.product_id === item.productId)
    );
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setEditedQuantity(0);
    setEditedUnitPrice(0);
    setEditedProductId("");
    setEditedDiscountPercent(0);
    setCurrentProductPriceRanges([]);
  };

  const handleProductChange = (newProductId: string) => {
    setEditedProductId(newProductId);

    // Auto-apply product-level discount
    const product = availableProducts.find((p) => p.id === newProductId);
    setEditedDiscountPercent(product?.discount_percent ?? 0);

    // Update price ranges for the new product
    const newPriceRanges = priceRanges.filter(
      (range) => range.product_id === newProductId
    );
    setCurrentProductPriceRanges(newPriceRanges);

    // Set default price from the first available price range
    if (newPriceRanges.length > 0) {
      const defaultRange =
        newPriceRanges.find(
          (range) => editedQuantity >= range.min && editedQuantity <= range.max
        ) || newPriceRanges[0];
      setEditedUnitPrice(defaultRange.price);
    }
  };

  const handleStartAddNew = () => {
    // Cancel any existing edits
    handleCancel();

    setIsAddingNew(true);
    setNewProductId("");
    setNewQuantity(1);
    setNewUnitPrice(0);
    setNewDiscountPercent(0);
    setNewProductPriceRanges([]);
  };

  const handleCancelNew = () => {
    setIsAddingNew(false);
    setNewProductId("");
    setNewQuantity(1);
    setNewUnitPrice(0);
    setNewDiscountPercent(0);
    setNewProductPriceRanges([]);
  };

  const handleNewProductChange = (productId: string) => {
    setNewProductId(productId);

    // Auto-apply product-level discount
    const product = availableProducts.find((p) => p.id === productId);
    setNewDiscountPercent(product?.discount_percent ?? 0);

    // Update price ranges for the new product
    const priceRangesForProduct = priceRanges.filter(
      (range) => range.product_id === productId
    );
    setNewProductPriceRanges(priceRangesForProduct);

    // Set default price from the first available price range
    if (priceRangesForProduct.length > 0) {
      const defaultRange =
        priceRangesForProduct.find(
          (range) => newQuantity >= range.min && newQuantity <= range.max
        ) || priceRangesForProduct[0];
      setNewUnitPrice(defaultRange.price);
    }
  };

  // Defensive guard: line items must never be mutated once a non-cancelled
  // factura has been issued for the order, even if the `editable` prop is stale.
  // Editing then would make the order diverge from the invoice, corrupting the
  // BI/Excel export and inflating the client's debt. Returns true if blocked.
  const isBlockedByActiveFactura = async (): Promise<boolean> => {
    if (!salesRequestId) return false;
    const { data, error } = await supabase
      .from("sales_request_documents")
      .select("id")
      .eq("sales_request_id", salesRequestId)
      .eq("document_type", "factura")
      .not("bsale_number", "is", null)
      .or("cancelled.is.null,cancelled.eq.false")
      .limit(1);

    // Fail open on read error: the editable prop is the primary guard.
    if (error || !data || data.length === 0) return false;

    toast({
      title: "Acción no permitida",
      description:
        "No se pueden modificar los productos: la nota ya tiene una factura emitida.",
      variant: "destructive",
    });
    return true;
  };

  const handleAddNew = async () => {
    if (
      !salesRequestId ||
      !newProductId ||
      newQuantity <= 0 ||
      newUnitPrice <= 0
    ) {
      toast({
        title: "Error",
        description: "Todos los campos deben estar completos y ser válidos",
        variant: "destructive",
      });
      return;
    }

    if (await isBlockedByActiveFactura()) return;

    setAddingSaving(true);
    try {
      // Add new sales request item to database
      const { data: newItem, error: itemError } = await supabase
        .from("sales_request_items")
        .insert({
          sales_request_id: salesRequestId,
          product_id: newProductId,
          quantity: newQuantity,
          unit_price: newUnitPrice,
          discount_percent: newDiscountPercent,
        })
        .select()
        .single();

      if (itemError) throw itemError;

      // Create the new item object for local state
      const newSalesRequestItem: SalesRequestItem = {
        id: newItem.id,
        salesRequestId: salesRequestId,
        productId: newProductId,
        quantity: newQuantity,
        unitPrice: newUnitPrice,
        discountPercent: newDiscountPercent,
        unit:
          availableProducts.find((p) => p.id === newProductId)?.unit_code ||
          availableProducts.find((p) => p.id === newProductId)
            ?.base_measurement_unit ||
          "",
      };

      // Update local state
      const updatedItems = [...items, newSalesRequestItem];

      // Calculate new totals
      const newTotalAmount = updatedItems.reduce(
        (sum, item) =>
          sum +
          calcLineTotal(
            item.unitPrice,
            item.quantity,
            item.discountPercent ?? 0
          ),
        0
      );

      // Update sales request totals in database
      const { error: requestError } = await supabase
        .from("sales_request")
        .update({
          total_amount: newTotalAmount,
        })
        .eq("id", salesRequestId);

      if (requestError) throw requestError;

      // Fetch the updated price_with_iva (generated column)
      const { data: updatedSalesRequest, error: fetchError } = await supabase
        .from("sales_request")
        .select("price_with_iva")
        .eq("id", salesRequestId)
        .single();

      if (fetchError) throw fetchError;

      const actualPriceWithIva =
        updatedSalesRequest?.price_with_iva ||
        Math.round(newTotalAmount * 1.19);

      // Add product details for the new product
      const newProduct = availableProducts.find((p) => p.id === newProductId);
      if (newProduct) {
        setProductDetails((prev) => ({
          ...prev,
          [newProductId]: {
            id: newProduct.id,
            name: newProduct.long_name || newProduct.short_name,
            image: newProduct.product_image_url,
            details:
              newProduct.short_name !== newProduct.long_name
                ? newProduct.short_name
                : undefined,
            unit: newProduct.unit_code || newProduct.base_measurement_unit,
          },
        }));
      }

      // Notify parent component with updated totals
      if (onItemsChange) {
        onItemsChange(updatedItems, newTotalAmount, actualPriceWithIva);
      }

      // Reset add new form
      handleCancelNew();

      toast({
        title: "Éxito",
        description: "Producto agregado correctamente",
      });
    } catch (error) {
      console.error("Error adding new item:", error);
      toast({
        title: "Error",
        description: "No se pudo agregar el producto",
        variant: "destructive",
      });
    } finally {
      setAddingSaving(false);
    }
  };

  const handleDelete = async (index: number) => {
    if (!salesRequestId) return;

    if (await isBlockedByActiveFactura()) return;

    setDeleting(index);
    try {
      const item = items[index];

      // Delete the sales request item from database
      const { error: deleteError } = await supabase
        .from("sales_request_items")
        .delete()
        .eq("id", item.id);

      if (deleteError) throw deleteError;

      // Update local state
      const updatedItems = items.filter((_, i) => i !== index);

      // Calculate new totals
      const newTotalAmount = updatedItems.reduce(
        (sum, item) =>
          sum +
          calcLineTotal(
            item.unitPrice,
            item.quantity,
            item.discountPercent ?? 0
          ),
        0
      );

      // Update sales request totals in database
      const { error: requestError } = await supabase
        .from("sales_request")
        .update({
          total_amount: newTotalAmount,
        })
        .eq("id", salesRequestId);

      if (requestError) throw requestError;

      // Fetch the updated price_with_iva (generated column)
      const { data: updatedSalesRequest, error: fetchError } = await supabase
        .from("sales_request")
        .select("price_with_iva")
        .eq("id", salesRequestId)
        .single();

      if (fetchError) throw fetchError;

      const actualPriceWithIva =
        updatedSalesRequest?.price_with_iva ||
        Math.round(newTotalAmount * 1.19);

      // Notify parent component with updated items and totals
      if (onItemsChange) {
        onItemsChange(updatedItems, newTotalAmount, actualPriceWithIva);
      }

      toast({
        title: "Éxito",
        description: "Producto eliminado correctamente",
      });
    } catch (error) {
      console.error("Error deleting item:", error);
      toast({
        title: "Error",
        description: "No se pudo eliminar el producto",
        variant: "destructive",
      });
    } finally {
      setDeleting(null);
    }
  };

  const handleSave = async (index: number) => {
    if (
      !salesRequestId ||
      editedQuantity <= 0 ||
      editedUnitPrice <= 0 ||
      !editedProductId
    ) {
      toast({
        title: "Error",
        description: "Todos los campos deben estar completos y ser válidos",
        variant: "destructive",
      });
      return;
    }

    if (await isBlockedByActiveFactura()) return;

    setSaving(true);
    try {
      const item = items[index];

      // Update sales request item in database
      const { error: itemError } = await supabase
        .from("sales_request_items")
        .update({
          quantity: editedQuantity,
          unit_price: editedUnitPrice,
          product_id: editedProductId,
          discount_percent: editedDiscountPercent,
        })
        .eq("id", item.id);

      if (itemError) throw itemError;

      // Update local state
      const updatedItems = [...items];
      updatedItems[index] = {
        ...item,
        quantity: editedQuantity,
        unitPrice: editedUnitPrice,
        productId: editedProductId,
        discountPercent: editedDiscountPercent,
      };

      // Calculate new totals
      const newTotalAmount = updatedItems.reduce(
        (sum, item) =>
          sum +
          calcLineTotal(
            item.unitPrice,
            item.quantity,
            item.discountPercent ?? 0
          ),
        0
      );

      // Update sales request totals in database
      const { error: requestError } = await supabase
        .from("sales_request")
        .update({
          total_amount: newTotalAmount,
        })
        .eq("id", salesRequestId);

      if (requestError) throw requestError;

      // Fetch the updated price_with_iva (generated column)
      const { data: updatedSalesRequest, error: fetchError } = await supabase
        .from("sales_request")
        .select("price_with_iva")
        .eq("id", salesRequestId)
        .single();

      if (fetchError) throw fetchError;

      const actualPriceWithIva =
        updatedSalesRequest?.price_with_iva ||
        Math.round(newTotalAmount * 1.19);

      // If product changed, update product details
      if (editedProductId !== item.productId) {
        const newProduct = availableProducts.find(
          (p) => p.id === editedProductId
        );
        if (newProduct) {
          setProductDetails((prev) => ({
            ...prev,
            [editedProductId]: {
              id: newProduct.id,
              name: newProduct.long_name || newProduct.short_name,
              image: newProduct.product_image_url,
              details:
                newProduct.short_name !== newProduct.long_name
                  ? newProduct.short_name
                  : undefined,
              unit: newProduct.unit_code || newProduct.base_measurement_unit,
            },
          }));
        }
      }

      // Notify parent component with updated totals
      if (onItemsChange) {
        onItemsChange(updatedItems, newTotalAmount, actualPriceWithIva);
      }

      setEditingIndex(null);
      setCurrentProductPriceRanges([]);

      // Show success message for item update
      toast({
        title: "Éxito",
        description: "Producto actualizado correctamente",
      });
    } catch (error) {
      console.error("Error updating item:", error);
      toast({
        title: "Error",
        description: "No se pudo actualizar el producto",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const headerBar = (
    <div className="flex items-center justify-between border-b border-gray-200 bg-gray-100 px-5 py-3">
      <h3 className="flex items-center text-lg font-semibold text-gray-800">
        <ShoppingBag className="mr-2 h-5 w-5 text-gray-600" />
        Productos
      </h3>
      {!loading && editable && !isAddingNew && editingIndex === null && (
        <Button size="sm" onClick={handleStartAddNew}>
          <Plus className="mr-1 h-4 w-4" />
          Agregar Producto
        </Button>
      )}
    </div>
  );

  if (loading) {
    return (
      <>
        {headerBar}
        <div className="p-4">
          <div className="mb-3 flex gap-4">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-3 flex-1" />
            ))}
          </div>
          <div className="space-y-3">
            {Array.from({ length: Math.max(items?.length ?? 0, 3) }).map(
              (_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-10 w-10 shrink-0 rounded-md" />
                  <Skeleton className="h-4 flex-1" />
                  <Skeleton className="h-4 w-14" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-10" />
                  <Skeleton className="h-4 w-20" />
                </div>
              )
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {headerBar}
      <div className="w-full overflow-auto">
        <table className="w-full caption-bottom divide-y divide-gray-200">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Producto
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                Cantidad
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                Precio Unitario
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                Dcto. %
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                Subtotal
              </th>
              {editable && (
                <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                  Acciones
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {/* Existing items */}
            {items &&
              items.length > 0 &&
              items.map((item, index) => {
                const product = productDetails[item.productId] || {
                  id: item.productId,
                  name: `Producto ${index + 1}`,
                  details: `ID: ${item.productId}`,
                  unit: item.unit,
                };

                return (
                  <React.Fragment key={index}>
                    <tr className="transition-colors hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">
                        {editingIndex === index ? (
                          <select
                            value={editedProductId}
                            onChange={(e) =>
                              handleProductChange(e.target.value)
                            }
                            disabled={saving}
                            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="">Seleccionar producto</option>
                            {getSelectableProducts(editedProductId).map(
                              (availableProduct) => (
                                <option
                                  key={availableProduct.id}
                                  value={availableProduct.id}
                                >
                                  {availableProduct.long_name ||
                                    availableProduct.short_name}
                                </option>
                              )
                            )}
                          </select>
                        ) : (
                          <div className="flex items-center">
                            {product.image ? (
                              <img
                                src={product.image}
                                alt={product.name}
                                className="mr-3 h-10 w-10 rounded-md border bg-gray-100 object-cover"
                              />
                            ) : (
                              <div className="mr-3 flex h-10 w-10 items-center justify-center rounded-md border bg-gray-100 text-gray-400">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                              </div>
                            )}
                            <div>
                              <div className="font-medium text-gray-800">
                                {nameSnapshots[item.id] || product.name}
                              </div>
                              {product.details && (
                                <div className="text-xs text-gray-500">
                                  {product.details}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-gray-800">
                        {editingIndex === index ? (
                          <Input
                            type="number"
                            inputMode="numeric"
                            min="1"
                            value={editedQuantity}
                            onChange={(e) =>
                              setEditedQuantity(parseInt(e.target.value) || 1)
                            }
                            onFocus={(e) => e.target.select()}
                            className="w-20 text-right"
                            disabled={saving}
                          />
                        ) : (
                          <div>
                            {item.quantity}
                            {product.unit && (
                              <span className="ml-1 text-xs text-gray-500">
                                {product.unit}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-gray-600">
                        {editingIndex === index ? (
                          <Input
                            type="number"
                            inputMode="decimal"
                            min="0"
                            step="0.0001"
                            value={editedUnitPrice}
                            onChange={(e) =>
                              setEditedUnitPrice(
                                parseFloat(e.target.value) || 0
                              )
                            }
                            onFocus={(e) => e.target.select()}
                            className="w-24 text-right"
                            disabled={saving}
                          />
                        ) : (
                          formatUnitPrice(item.unitPrice)
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-gray-600">
                        {editingIndex === index
                          ? editedDiscountPercent > 0
                            ? `${editedDiscountPercent}%`
                            : "-"
                          : item.discountPercent
                            ? `${item.discountPercent}%`
                            : "-"}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-semibold text-gray-800">
                        {editingIndex === index
                          ? formatCurrency(
                              calcLineTotal(
                                editedUnitPrice,
                                editedQuantity,
                                editedDiscountPercent
                              )
                            )
                          : formatCurrency(
                              calcLineTotal(
                                item.unitPrice,
                                item.quantity,
                                item.discountPercent ?? 0
                              )
                            )}
                      </td>
                      {editable && (
                        <td className="px-4 py-3 text-center">
                          {editingIndex === index ? (
                            <div className="flex items-center justify-center space-x-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleSave(index)}
                                disabled={saving}
                                className="h-8 w-8 p-0"
                              >
                                <Check className="h-4 w-4 text-green-600" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={handleCancel}
                                disabled={saving}
                                className="h-8 w-8 p-0"
                              >
                                <X className="h-4 w-4 text-red-600" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center space-x-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEdit(index, item)}
                                disabled={deleting !== null || isAddingNew}
                                className="h-8 w-8 p-0"
                              >
                                <Edit2 className="h-4 w-4 text-blue-600" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDelete(index)}
                                disabled={deleting !== null || isAddingNew}
                                className="h-8 w-8 p-0"
                              >
                                <Trash2
                                  className={`h-4 w-4 ${
                                    deleting === index
                                      ? "text-gray-400"
                                      : "text-red-600 hover:text-red-700"
                                  }`}
                                />
                              </Button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                    {/* Show price ranges when editing this item */}
                    {editingIndex === index &&
                      currentProductPriceRanges.length > 0 && (
                        <tr>
                          <td
                            colSpan={editable ? 6 : 5}
                            className="bg-muted/30 px-4 py-2"
                          >
                            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-muted-foreground">
                              <Info size={16} />
                              <span>
                                Rangos de precios disponibles para{" "}
                                {availableProducts.find(
                                  (p) => p.id === editedProductId
                                )?.short_name || product.name}
                                :
                              </span>
                            </div>
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                              {currentProductPriceRanges.map((range, idx) => (
                                <div key={idx} className="text-sm">
                                  <span className="font-medium">
                                    {formatCurrency(range.price)}
                                  </span>
                                  <span className="ml-1 text-muted-foreground">
                                    (
                                    {range.min.toLocaleString(undefined, {
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 1,
                                    })}{" "}
                                    -{" "}
                                    {range.max === 2147483647
                                      ? "∞"
                                      : range.max.toLocaleString(undefined, {
                                          minimumFractionDigits: 0,
                                          maximumFractionDigits: 1,
                                        })}{" "}
                                    {availableProducts.find(
                                      (p) => p.id === editedProductId
                                    )?.unit_code ||
                                      availableProducts.find(
                                        (p) => p.id === editedProductId
                                      )?.base_measurement_unit ||
                                      product.unit}
                                    )
                                  </span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                  </React.Fragment>
                );
              })}

            {/* Add new product row */}
            {isAddingNew && (
              <React.Fragment>
                <tr className="bg-blue-50 transition-colors">
                  <td className="px-4 py-3 text-sm">
                    <select
                      value={newProductId}
                      onChange={(e) => handleNewProductChange(e.target.value)}
                      disabled={addingSaving}
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Seleccionar producto</option>
                      {getSelectableProducts().map((availableProduct) => (
                        <option
                          key={availableProduct.id}
                          value={availableProduct.id}
                        >
                          {availableProduct.long_name ||
                            availableProduct.short_name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-medium text-gray-800">
                    <Input
                      type="number"
                      inputMode="numeric"
                      min="1"
                      value={newQuantity}
                      onChange={(e) =>
                        setNewQuantity(parseInt(e.target.value) || 1)
                      }
                      onFocus={(e) => e.target.select()}
                      className="w-20 text-right"
                      disabled={addingSaving}
                    />
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-600">
                    <Input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.0001"
                      value={newUnitPrice}
                      onChange={(e) =>
                        setNewUnitPrice(parseFloat(e.target.value) || 0)
                      }
                      onFocus={(e) => e.target.select()}
                      className="w-24 text-right"
                      disabled={addingSaving}
                    />
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-600">
                    {newDiscountPercent > 0 ? `${newDiscountPercent}%` : "-"}
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-semibold text-gray-800">
                    {formatCurrency(
                      calcLineTotal(
                        newUnitPrice,
                        newQuantity,
                        newDiscountPercent
                      )
                    )}
                  </td>
                  {editable && (
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleAddNew}
                          disabled={addingSaving}
                          className="h-8 w-8 p-0"
                        >
                          <Check className="h-4 w-4 text-green-600" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleCancelNew}
                          disabled={addingSaving}
                          className="h-8 w-8 p-0"
                        >
                          <X className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
                {/* Show price ranges for new product */}
                {newProductPriceRanges.length > 0 && (
                  <tr>
                    <td
                      colSpan={editable ? 6 : 5}
                      className="bg-blue-50/50 px-4 py-2"
                    >
                      <div className="mb-2 flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <Info size={16} />
                        <span>
                          Rangos de precios disponibles para{" "}
                          {availableProducts.find((p) => p.id === newProductId)
                            ?.short_name || "producto seleccionado"}
                          :
                        </span>
                      </div>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        {newProductPriceRanges.map((range, idx) => (
                          <div key={idx} className="text-sm">
                            <span className="font-medium">
                              {formatCurrency(range.price)}
                            </span>
                            <span className="ml-1 text-muted-foreground">
                              (
                              {range.min.toLocaleString(undefined, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 1,
                              })}{" "}
                              -{" "}
                              {range.max === 2147483647
                                ? "∞"
                                : range.max.toLocaleString(undefined, {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 1,
                                  })}{" "}
                              {availableProducts.find(
                                (p) => p.id === newProductId
                              )?.unit_code ||
                                availableProducts.find(
                                  (p) => p.id === newProductId
                                )?.base_measurement_unit}
                              )
                            </span>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            )}

            {/* Empty state */}
            {(!items || items.length === 0) && !isAddingNew && (
              <tr>
                <td
                  colSpan={editable ? 6 : 5}
                  className="px-4 py-8 text-center text-sm text-muted-foreground"
                >
                  No hay productos en esta nota de venta
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};
